import { useEffect, useRef, useState } from 'react'
import type { AnimationEvent, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Maximize2, X } from 'lucide-react'
import { Button } from '@shared/design-system/ui'
import { useAdminLiveStageIdle, useAdminScreenWakeLock } from './admin-live-view'
import * as styles from './admin-chart-stage.css'

/**
 * Tela cheia do gráfico, com a mesma entrada do modo foco do app do vendedor. Serve para
 * deixar o painel aberto num telão, então traz a marca em cima e o gráfico no resto da área.
 */
export function AdminChartStage({
  open,
  onClose,
  title,
  subtitle,
  children,
  liveMode = false,
  testId,
}: {
  open: boolean
  onClose: () => void
  title: string
  subtitle: string
  children: ReactNode
  liveMode?: boolean
  testId?: string
}) {
  const dialogRef = useRef<HTMLElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const onCloseRef = useRef(onClose)
  const [keepMounted, setKeepMounted] = useState(open)
  const idle = useAdminLiveStageIdle(open && liveMode)
  useAdminScreenWakeLock(open && liveMode)

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (!open) return
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onCloseRef.current()
        return
      }
      if (event.key !== 'Tab') return
      const dialog = dialogRef.current
      if (!dialog) return
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      )
      const first = focusable[0]
      const last = focusable.at(-1)
      if (!first || !last) return
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    const previousOverflow = document.body.style.overflow
    const previousFocus = document.activeElement
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', handleKeyDown)
    closeRef.current?.focus()
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKeyDown)
      if (previousFocus instanceof HTMLElement) previousFocus.focus()
    }
  }, [open])

  if (!open && !keepMounted) return null

  const state = open ? 'open' : 'closing'

  return createPortal(
    <div
      className={styles.backdrop}
      data-state={state}
      data-live-mode={liveMode ? 'true' : undefined}
      data-live-idle={liveMode && idle ? 'true' : undefined}
      role="presentation"
      onAnimationStart={(event: AnimationEvent<HTMLDivElement>) => {
        if (open && event.target === event.currentTarget) setKeepMounted(true)
      }}
      onAnimationEnd={(event: AnimationEvent<HTMLDivElement>) => {
        if (!open && event.target === event.currentTarget) setKeepMounted(false)
      }}
      onClick={() => onCloseRef.current()}
    >
      <section
        ref={dialogRef}
        className={styles.shell}
        data-state={state}
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
        aria-label={title}
        data-testid={testId}
        onClick={(event) => {
          event.stopPropagation()
        }}
      >
        <header className={liveMode ? `${styles.topbar} ${styles.liveTopbar}` : styles.topbar}>
          <span className={styles.brand}>
            <img
              className={`${styles.brandLogo} ${styles.brandLogoLight}`}
              src="/brand/logo-full.png"
              alt="VendePay"
            />
            <img
              className={`${styles.brandLogo} ${styles.brandLogoDark}`}
              src="/brand/logo-full-claro.png"
              alt="VendePay"
            />
          </span>
          <span className={styles.heading}>
            <h2 className={styles.title}>{title}</h2>
            <span className={styles.subtitle}>{subtitle}</span>
          </span>
          <button
            ref={closeRef}
            type="button"
            className={styles.closeButton}
            aria-label="Sair da tela cheia"
            onClick={() => onCloseRef.current()}
          >
            <X size={18} />
          </button>
        </header>
        <div className={liveMode ? `${styles.content} ${styles.liveContent}` : styles.content}>
          {children}
        </div>
      </section>
    </div>,
    document.body,
  )
}

export function AdminChartStageTrigger({ onOpen }: { onOpen: () => void }) {
  return (
    <Button
      variant="text"
      className={styles.trigger}
      aria-label="Abrir o gráfico em tela cheia"
      onClick={onOpen}
    >
      <Maximize2 size={20} aria-hidden />
    </Button>
  )
}
